import { Link } from "react-router-dom";
import { Users, ClipboardList, Dna, Check, ArrowRight, Trophy, Circle } from "lucide-react";

/**
 * Compact 4-step onboarding for the new-user dashboard.
 *
 * Props:
 *   greetingName   {string|null}   — first-name-style greeting, or null → "Welcome"
 *   hasTeam        {boolean}
 *   hasPlayers     {boolean}
 *   hasPractice    {boolean}
 *   hasDna         {boolean}       — whether the user has seen/started Coach DNA
 */
export default function OnboardingChecklist({ greetingName, hasTeam, hasPlayers, hasPractice, hasDna }) {
  const steps = [
    {
      id: "team",
      icon: Trophy,
      title: "Create your team",
      desc: "Add your team name, age group, sport and season.",
      cta: "Create team",
      to: "/teams",
      done: hasTeam,
      testid: "onb-step-team",
      primary: true,
    },
    {
      id: "players",
      icon: Users,
      title: "Add your players",
      desc: "Build your roster so practices and player development tools can personalise around your team.",
      cta: "Add players",
      to: "/players",
      done: hasPlayers,
      testid: "onb-step-players",
    },
    {
      id: "practice",
      icon: ClipboardList,
      title: "Build your first practice",
      desc: "Tell CoachIQ what you want to work on and generate your first session.",
      cta: "Build practice",
      to: "/practices",
      done: hasPractice,
      testid: "onb-step-practice",
    },
    {
      id: "dna",
      icon: Dna,
      title: "Discover your Coach DNA",
      desc: "Start building a picture of your coaching style, strengths and growth opportunities.",
      cta: "Open Coach DNA",
      to: "/coach-dna",
      done: hasDna,
      testid: "onb-step-dna",
    },
  ];

  // First uncompleted step is the "active" one. Everything after it stays visually secondary.
  const activeIdx = steps.findIndex((s) => !s.done);
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="max-w-3xl mx-auto animate-slide-up" data-testid="dashboard-onboarding">
      <div className="mb-6">
        <div className="font-display text-3xl md:text-4xl font-bold tracking-tight text-gray-900">
          {greetingName ? <>Welcome, {greetingName} <span aria-hidden>👋</span></> : <>Welcome <span aria-hidden>👋</span></>}
        </div>
        <div className="mt-2 text-gray-500">Let's get CoachIQ ready for your season.</div>
        <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-gray-500">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#007AFF] to-emerald-500 transition-all duration-500"
              style={{ width: `${(doneCount / steps.length) * 100}%` }}
              data-testid="onb-progress-bar"
            />
          </div>
          <span data-testid="onb-progress-count">{doneCount}/{steps.length}</span>
        </div>
      </div>

      <ol className="space-y-3">
        {steps.map((s, i) => {
          const isActive = i === activeIdx;
          const isPending = !s.done && !isActive;
          return (
            <li
              key={s.id}
              data-testid={s.testid}
              data-state={s.done ? "done" : isActive ? "active" : "pending"}
              className={`relative rounded-3xl border transition-all
                ${s.done ? "bg-emerald-50/40 border-emerald-100" : ""}
                ${isActive ? "bg-white border-gray-200 shadow-sm ring-1 ring-[#007AFF]/10" : ""}
                ${isPending ? "bg-white/60 border-gray-100" : ""}`}
            >
              <div className={`flex items-start gap-4 p-5 md:p-6 ${isPending ? "opacity-60" : ""}`}>
                <div className={`shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center
                  ${s.done ? "bg-emerald-500 text-white" : isActive ? "bg-[#007AFF] text-white" : "bg-gray-100 text-gray-400"}`}
                >
                  {s.done ? <Check className="w-5 h-5" strokeWidth={2.5} /> : <s.icon className="w-5 h-5" strokeWidth={2.2} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`text-[10px] font-bold uppercase tracking-widest ${s.done ? "text-emerald-600" : isActive ? "text-[#007AFF]" : "text-gray-400"}`}>
                      Step {i + 1}{s.done ? " · Done" : isActive ? " · Now" : ""}
                    </div>
                  </div>
                  <div className={`mt-0.5 font-display text-lg font-bold ${s.done ? "text-emerald-900" : "text-gray-900"}`}>
                    {s.title}
                  </div>
                  <div className={`mt-1 text-sm leading-relaxed ${s.done ? "text-emerald-800/80" : "text-gray-600"}`}>
                    {s.desc}
                  </div>

                  {!s.done && (
                    <div className="mt-3">
                      <Link
                        to={s.to}
                        data-testid={`${s.testid}-cta`}
                        className={`inline-flex items-center gap-2 rounded-full text-sm font-semibold transition-colors
                          ${isActive
                            ? "bg-[#007AFF] hover:bg-[#0056B3] text-white px-5 py-2.5 shadow-lg shadow-blue-500/20"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2"}`}
                      >
                        {s.cta} <ArrowRight className={isActive ? "w-4 h-4" : "w-3.5 h-3.5"} />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-6 text-center text-xs text-gray-400">
        We'll hide this once you're set up — you can always revisit Coach DNA any time.
      </div>
    </div>
  );
}
